/**
 * CriteriaResultCard Component
 *
 * Displays an individual criterion evaluation result with all details:
 * - Criterion number and text
 * - Result badge with color coding
 * - Confidence indicator
 * - Page citations as clickable links
 * - Expandable AI reasoning section
 * - Human verification status and controls
 * - Contradiction warning
 */

import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  HelpCircle,
  Search,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import type { CriterionResult, Citation, ResultType } from '../../types';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { PageCitationLinks } from './PageCitationLinks';
import { ExcerptDisplay } from './ExcerptDisplay';
import { ConflictWarning } from './ConflictWarning';
import { NoEvidenceExplanation } from './NoEvidenceExplanation';

interface CriteriaResultCardProps {
  result: CriterionResult;
  onCitationClick: (citation: Citation) => void;
  onVerify: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

// Result type styling
const RESULT_STYLES: Record<
  ResultType,
  {
    bg: string;
    text: string;
    border: string;
    icon: typeof CheckCircle;
    label: string;
  }
> = {
  met: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    icon: CheckCircle,
    label: 'Met',
  },
  not_met: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: XCircle,
    label: 'Not Met',
  },
  uncertain: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    icon: HelpCircle,
    label: 'Uncertain',
  },
  not_found: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-300 dark:border-gray-600',
    icon: Search,
    label: 'Not Found',
  },
  contradictory: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800',
    icon: AlertTriangle,
    label: 'Contradictory',
  },
};

export function CriteriaResultCard({
  result,
  onCitationClick,
  onVerify,
  isExpanded: externalExpanded,
  onToggleExpand,
}: CriteriaResultCardProps) {
  // Internal expansion state (used if no external control)
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = externalExpanded ?? internalExpanded;

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const resultStyle = RESULT_STYLES[result.result];
  const ResultIcon = resultStyle.icon;

  // Generate criterion number display (e.g., "I-1" or "E-3")
  const criterionPrefix = result.type === 'inclusion' ? 'I' : 'E';
  const criterionDisplay = `${criterionPrefix}-${result.criterion_number}`;

  // Check if human verified with override
  const hasOverride =
    result.human_verified && result.human_result && result.human_result !== result.result;

  return (
    <div
      className={`rounded-lg border ${resultStyle.border} ${resultStyle.bg} overflow-hidden transition-all`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Criterion info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {/* Criterion number badge */}
              <span
                className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded ${
                  result.type === 'inclusion'
                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                    : 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                }`}
              >
                {criterionDisplay}
              </span>

              {/* Result badge */}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${resultStyle.bg} ${resultStyle.text} border ${resultStyle.border}`}
              >
                <ResultIcon size={12} />
                {resultStyle.label}
              </span>

              {/* Human verified badge */}
              {result.human_verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                  <ShieldCheck size={12} />
                  Verified
                  {hasOverride && ' (Override)'}
                </span>
              )}

              {/* Contradiction warning indicator */}
              {result.has_contradiction && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                  <AlertTriangle size={12} />
                  Conflict
                </span>
              )}
            </div>

            {/* Criterion text */}
            <p className="text-sm text-gray-800 dark:text-gray-200">{result.criterion_text}</p>

            {/* Citations and confidence row */}
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              <ConfidenceIndicator
                level={result.confidence_level}
                score={result.confidence_score}
              />
              {result.evidence && result.evidence.length > 0 && (
                <PageCitationLinks
                  citations={result.evidence}
                  onPageClick={(pageNum, citation) => onCitationClick(citation)}
                />
              )}
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Verify button (only if not yet verified) */}
            {!result.human_verified && (
              <button
                onClick={onVerify}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-400 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors min-h-[44px] sm:min-h-0"
              >
                <ShieldCheck size={14} />
                Verify
              </button>
            )}

            {/* Expand/collapse button */}
            <button
              onClick={toggleExpand}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/50 rounded transition-colors"
              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
            >
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-200/50 dark:border-gray-700/50 space-y-4">
          {/* Contradiction warning */}
          {result.has_contradiction && (
            <div className="mt-3">
              <ConflictWarning
                hasContradiction={result.has_contradiction}
                reasoning={result.ai_reasoning}
              />
            </div>
          )}

          {/* No evidence explanation (for not_found results) */}
          {result.result === 'not_found' && (
            <div className="mt-3">
              <NoEvidenceExplanation criterionText={result.criterion_text} />
            </div>
          )}

          {/* AI Reasoning */}
          {result.ai_reasoning && result.result !== 'not_found' && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">AI Reasoning</h4>
              <div className="bg-white/70 dark:bg-gray-800/60 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-300">{result.ai_reasoning}</p>
              </div>
            </div>
          )}

          {/* Evidence excerpts */}
          {result.evidence && result.evidence.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Source Evidence ({result.evidence.length} citation
                {result.evidence.length !== 1 ? 's' : ''})
              </h4>
              <div className="space-y-2">
                {result.evidence.map((citation, index) => (
                  <div key={citation.citation_id || index} className="bg-white/70 dark:bg-gray-800/60 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <button
                        onClick={() => onCitationClick(citation)}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                      >
                        Page {citation.page_number}
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{citation.evidence_type}</span>
                    </div>
                    <ExcerptDisplay text={citation.extracted_text} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Human verification details (if verified) */}
          {result.human_verified && (
            <div className="mt-3 bg-green-50/70 rounded-lg p-3 border border-green-200 dark:border-green-800">
              <h4 className="text-xs font-medium text-green-700 dark:text-green-300 mb-2">Human Verification</h4>
              <div className="space-y-1 text-sm">
                {hasOverride && (
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Override result:</span>{' '}
                    <span
                      className={`font-medium ${
                        RESULT_STYLES[result.human_result!]?.text || 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {RESULT_STYLES[result.human_result!]?.label || result.human_result}
                    </span>
                  </p>
                )}
                {result.human_notes && (
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Notes:</span> {result.human_notes}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
